// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Component } from './component';
import render from './render';
import emptyFn from './emptyFn';
import { ESC, TAB } from './keyCode';

import './modal-dialog-box.less';

const FOCUSABLE_SELECTOR = '[tabindex]:not([tabindex="-1"]), '
    + 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
    + 'select:not([disabled]), textarea:not([disabled])';

export class ModalDialogBox extends Component {
    _initConfiguration(config) {
        super._initConfiguration(config);
        this._focusTimeoutId = null;

        this._screenDisablerId = this._getConfigParam('screenDisablerId', 'screenDisabler');
        this._hideOnEscape = this._getConfigParam('hideOnEscape', false);
        this._hideOnBgClick = this._getConfigParam('hideOnBgClick', true);
        this._onShow = this._getConfigParam('onShow', emptyFn);
        this._onHide = this._getConfigParam('onHide', emptyFn);

        if (!document.getElementById(this._screenDisablerId)) {
            ModalDialogBox.initScrollbarSpacer();
            render(
                document.body,
                `<div id="${this._screenDisablerId}" class="main-disabled-block"></div>`,
                'top',
            );
        }

        this._screenDisabler = document.getElementById(this._screenDisablerId);

        const renderTo = this._getConfigParam('renderTo', 'modalDialogBox');
        this._renderTarget = typeof renderTo === 'string' ? document.getElementById(renderTo) : renderTo;
        if (!this._renderTarget) {
            this._renderTarget = document.createElement('div');
            this._renderTarget.id = renderTo;
        }

        this._renderTarget.innerHTML = '';
        render(this._screenDisabler, this._renderTarget, 'after');
        this._onKeyDownhandler = this._onKeyDown.bind(this);
    }

    _addEvents() {
        super._addEvents();

        if (this._hideOnBgClick) {
            this._componentElement.addEventListener('click', () => {
                this.hide();
            });
        }

        const popupContainer = this._componentElement.querySelector('.popup-container');
        if (popupContainer) {
            popupContainer.addEventListener('click', event => {
                event.stopPropagation();
            });
        }

        this._onKeyDownhandler = this._onKeyDown.bind(this);
    }

    show() {
        super.show();
        document.querySelector('html').classList.add('modal-open');
        document.addEventListener('keydown', this._onKeyDownhandler);
        this._onShow();
        this._focusFirstElement();
    }

    _focusFirstElement(delay = 300) {
        if (this._focusTimeoutId) {
            clearTimeout(this._focusTimeoutId);
        }
        this._focusTimeoutId = setTimeout(() => {
            this._focusTimeoutId = null;
            const el = this._componentElement;

            const autofocus = el.querySelector('[autofocus]');
            if (autofocus) {
                autofocus.focus({ preventScroll: true });
                return;
            }

            const focusable = this._getFocusableElements()[0];
            if (focusable) {
                focusable.focus({ preventScroll: true });
                return;
            }

            const container = el.querySelector('.popup-container');
            if (container) {
                const previousTabIndex = container.getAttribute('tabindex');
                container.setAttribute('tabindex', '-1');
                container.focus({ preventScroll: true });
                container.addEventListener('blur', () => {
                    if (previousTabIndex === null) {
                        container.removeAttribute('tabindex');
                    } else {
                        container.setAttribute('tabindex', previousTabIndex);
                    }
                }, { once: true });
            }
        }, delay);
    }

    hide() {
        if (this._focusTimeoutId) {
            clearTimeout(this._focusTimeoutId);
            this._focusTimeoutId = null;
        }
        super.hide();
        document.querySelector('html').classList.remove('modal-open');
        document.removeEventListener('keydown', this._onKeyDownhandler);
        this._onHide();
    }

    _onKeyDown(e) {
        switch (e.keyCode) {
            case (this._hideOnEscape && ESC):
                this.hide();
                break;
            case TAB:
                if (!e.defaultPrevented) {
                    this._trapFocus(e);
                }
                break;
        }
    }

    _getFocusableElements() {
        return Array.from(this._componentElement.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter(el => {
                const style = el.ownerDocument.defaultView.getComputedStyle(el);
                return el.tabIndex >= 0
                    && !el.matches(':disabled')
                    && !(el.tagName === 'INPUT' && el.type === 'hidden')
                    && el.getClientRects().length > 0
                    && style.visibility !== 'hidden';
            })
            .sort((a, b) => {
                const aTabIndex = a.tabIndex;
                const bTabIndex = b.tabIndex;
                if (aTabIndex > 0 && bTabIndex > 0) {
                    return aTabIndex - bTabIndex;
                }
                if (aTabIndex > 0) {
                    return -1;
                }
                if (bTabIndex > 0) {
                    return 1;
                }
                return 0;
            });
    }

    _trapFocus(e) {
        if (!this._componentElement.isConnected) {
            return;
        }

        const focusable = this._getFocusableElements();

        if (!focusable.length) {
            e.preventDefault();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = this._componentElement.ownerDocument.activeElement;

        if (e.shiftKey && (active === first || !focusable.includes(active))) {
            e.preventDefault();
            last.focus({ preventScroll: true });
        } else if (!e.shiftKey && (active === last || !focusable.includes(active))) {
            e.preventDefault();
            first.focus({ preventScroll: true });
        }
    }
}

ModalDialogBox.initScrollbarSpacer = () => {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '50px';
    scrollDiv.style.height = '50px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';
    document.body.appendChild(scrollDiv);

    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);

    const styleEl = document.createElement('style');
    document.head.appendChild(styleEl);
    styleEl.sheet.insertRule(`.modal-open, .modal-open .pul-layout__header-inner {padding-inline-end: ${scrollbarWidth}px}`, 0);
    styleEl.sheet.insertRule(`.modal-open .js-scrollbar-spacer {margin-right: ${scrollbarWidth}px}`, 1);
};
